import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, UserPlus, Phone, ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useLeadSources } from '@/hooks/useReferenceData';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { cn } from '@/lib/utils';
import { EstimateFromLeadDialog } from './EstimateFromLeadDialog';
import { CreateLeadDialog } from '@/components/leads/CreateLeadDialog';
import type { LeadFormData } from '@/schemas/lead.schema';

/**
 * Entry point for "New estimate" from anywhere that is not already a lead.
 *
 * Every estimate belongs to a lead (the lead is the opportunity and carries the
 * campaign attribution; estimates.lead_id is NOT NULL). So this dialog only
 * ever does two things: pick an existing open lead, or create a lead first and
 * then open the estimate form for it. There is no path to a lead-less quote.
 */

interface CustomerSeed {
  id: string;
  customer_name: string;
  primary_phone?: string | null;
  primary_email?: string | null;
  primary_address?: string | null;
}

interface NewEstimateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When opened from a customer page: limit the lead list to that customer and pre-fill a new lead */
  customer?: CustomerSeed;
}

interface PickableLead {
  id: string;
  lead_number: string;
  customer_name: string;
  customer_phone: string;
  customer_email: string | null;
  customer_address: string | null;
  issue_description: string;
  quoted_price: number | null;
  customer_id: string | null;
  status: string;
  created_at: string;
  campaign: { name: string } | null;
}

const STATUS_LABEL: Record<string, string> = {
  new: 'New',
  needs_follow_up: 'Follow-up',
  estimate_sent: 'Estimate sent',
  estimate_accepted: 'Accepted',
  processing: 'In progress',
  converted: 'Booked',
};

function useLeadPicker(search: string, customerId?: string, enabled = true) {
  const term = search.trim();
  return useQuery({
    queryKey: ['estimate-lead-picker', term, customerId ?? null],
    enabled,
    staleTime: 15_000,
    queryFn: async (): Promise<PickableLead[]> => {
      let query = supabase
        .from('leads')
        .select('id, lead_number, customer_name, customer_phone, customer_email, customer_address, issue_description, quoted_price, customer_id, status, created_at, campaign:campaigns(name)')
        .neq('status', 'lost')
        .order('created_at', { ascending: false })
        .limit(customerId ? 25 : 15);
      if (customerId) query = query.eq('customer_id', customerId);
      if (term) {
        const digits = term.replace(/\D/g, '');
        const parts = [`customer_name.ilike.%${term}%`, `lead_number.ilike.%${term}%`];
        if (digits.length >= 3) parts.push(`customer_phone.ilike.%${digits}%`);
        query = query.or(parts.join(','));
      }
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as unknown as PickableLead[];
    },
  });
}

export function NewEstimateDialog({ open, onOpenChange, customer }: NewEstimateDialogProps) {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState<PickableLead | null>(null);
  const [showNewLead, setShowNewLead] = useState(false);
  const { formatDate } = useTimezoneUtils();
  const { data: leadSources = [] } = useLeadSources();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setDebounced('');
      setSelected(null);
    }
  }, [open]);

  const { data: leads = [], isLoading } = useLeadPicker(debounced, customer?.id, open);

  // Quotes raised for a known customer count as repeat business, not new advertising leads
  const repeatSourceId = useMemo(
    () => leadSources.find((s) => /repeat|existing/i.test(s.name))?.id ?? '',
    [leadSources],
  );

  const prefill: Partial<LeadFormData> | undefined = customer
    ? {
        customer_name: customer.customer_name,
        customer_phone: customer.primary_phone ?? '',
        customer_email: customer.primary_email ?? '',
        customer_address: customer.primary_address ?? '',
        lead_source_id: repeatSourceId,
      }
    : undefined;

  const openNewLead = () => {
    onOpenChange(false);
    setShowNewLead(true);
  };

  return (
    <>
      <Dialog open={open && !selected} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>New estimate</DialogTitle>
            <DialogDescription>
              {customer
                ? `Pick the enquiry this quote is for, or start a new one for ${customer.customer_name}.`
                : 'Every quote belongs to a lead. Pick the enquiry it is for, or create the lead first.'}
            </DialogDescription>
          </DialogHeader>

          {!customer && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, phone or lead number"
                className="pl-9"
              />
            </div>
          )}

          <div className="max-h-[50vh] overflow-y-auto rounded-lg border">
            {isLoading ? (
              <div className="space-y-2 p-3">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : leads.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                {debounced ? 'No open leads match that search.' : customer ? 'No open enquiries for this customer.' : 'No open leads yet.'}
              </div>
            ) : (
              <ul className="divide-y">
                {leads.map((lead) => (
                  <li key={lead.id}>
                    <button
                      type="button"
                      onClick={() => setSelected(lead)}
                      className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/50 focus:outline-none focus-visible:bg-muted/50"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium">{lead.customer_name}</span>
                          <span className="text-xs text-muted-foreground">{lead.lead_number}</span>
                          <Badge
                            variant="outline"
                            className={cn(
                              'border-transparent text-[10px]',
                              lead.status === 'converted' ? 'bg-success/10 text-success' : 'bg-info/10 text-info',
                            )}
                          >
                            {STATUS_LABEL[lead.status] ?? lead.status}
                          </Badge>
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{lead.customer_phone}</span>
                          <span>{formatDate(lead.created_at, 'd MMM')}</span>
                          {lead.campaign?.name && <span className="truncate">{lead.campaign.name}</span>}
                        </div>
                        <p className="mt-0.5 truncate text-xs text-muted-foreground/80">{lead.issue_description}</p>
                      </div>
                      <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex items-center justify-between border-t pt-3">
            <p className="text-xs text-muted-foreground">
              {customer ? 'Not one of these?' : 'Customer not in the list?'}
            </p>
            <Button type="button" variant="outline" size="sm" onClick={openNewLead} className="gap-2">
              <UserPlus className="h-4 w-4" />
              {customer ? 'New enquiry for this customer' : 'Create lead, then quote'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {selected && (
        <EstimateFromLeadDialog
          open={!!selected}
          onOpenChange={(o) => {
            if (!o) {
              setSelected(null);
              onOpenChange(false);
            }
          }}
          lead={{
            id: selected.id,
            customer_name: selected.customer_name,
            customer_phone: selected.customer_phone,
            customer_email: selected.customer_email,
            customer_address: selected.customer_address,
            issue_description: selected.issue_description,
            quoted_price: selected.quoted_price,
            customer_id: selected.customer_id,
          }}
        />
      )}

      {/* "Create Estimate" inside this dialog creates the lead, then opens the estimate form for it */}
      <CreateLeadDialog
        open={showNewLead}
        onOpenChange={setShowNewLead}
        prefill={prefill}
        customerId={customer?.id ?? null}
      />
    </>
  );
}
